import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CategoryPill } from '@/components/shared/CategoryPill';
import { MetaText } from '@/components/shared/MetaText';
import { Article } from '@/lib/types';
import { cn } from '@/lib/utils';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
  showCategory?: boolean;
  showExcerpt?: boolean;
}

/**
 * ArticleCard - Standardized article preview component
 * 
 * Uses design tokens for consistent typography and spacing.
 * Mobile-first: single column stacked layout.
 * Images preserve aspect ratio.
 */
export const ArticleCard = memo(function ArticleCard({ 
  article, 
  variant = 'default',
  showCategory = true,
  showExcerpt = true 
}: ArticleCardProps) {
  const isFeatured = variant === 'featured';
  const isCompact = variant === 'compact';

  const getArticleUrl = () => {
    switch (article.content_type) {
      case 'review':
        return `/reviews/${article.slug}`;
      case 'comparison':
        return `/comparisons/${article.slug}`;
      default:
        return `/news/${article.slug}`;
    }
  };

  const excerpt = article.body 
    ? article.body.replace(/<[^>]+>/g, '').slice(0, 150) + '...'
    : '';

  return (
    <Card className={cn(
      "group overflow-hidden transition-all hover:shadow-lg",
      isFeatured && "md:flex md:flex-row",
      isCompact && "flex flex-row"
    )}>
      {/* Image */}
      <Link 
        to={getArticleUrl()}
        className={cn(
          "block overflow-hidden flex-shrink-0",
          isFeatured && "md:w-1/2",
          isCompact && "w-20 h-20 sm:w-24 sm:h-24"
        )}
      >
        <div className={cn(
          "relative bg-muted",
          isFeatured ? "aspect-[16/10] md:aspect-auto md:h-full" : isCompact ? "h-full" : "aspect-[16/10]"
        )}>
          {article.thumbnail_url ? (
            <img
              src={article.thumbnail_url}
              alt={article.title}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
              decoding="async"
              width={isCompact ? 96 : 400}
              height={isCompact ? 96 : 250}
              sizes={isCompact ? '96px' : isFeatured ? '(max-width: 768px) 100vw, 50vw' : '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px'}
              style={{ contentVisibility: 'auto' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <span className="text-2xl md:text-4xl">🚗</span>
            </div>
          )}
          {article.is_featured && !isCompact && (
            <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground text-[length:var(--text-xs)]">
              Featured
            </Badge>
          )}
        </div>
      </Link>

      {/* Content */}
      <CardContent className={cn(
        "flex flex-col",
        isFeatured 
          ? "p-[var(--space-4)] md:p-[var(--space-6)] md:w-1/2" 
          : isCompact 
            ? "p-[var(--space-2)] sm:p-[var(--space-3)] flex-1 min-w-0" 
            : "p-[var(--space-3)] md:p-[var(--space-4)]"
      )}>
        {/* Category - Using standardized CategoryPill */}
        {showCategory && article.categories && article.categories[0] && !isCompact && (
          <div className="mb-[var(--space-2)]">
            <CategoryPill 
              name={article.categories[0].name}
              slug={article.categories[0].slug}
              variant="muted"
              size="sm"
            />
          </div>
        )}

        {/* Title - Token-based sizing */}
        <Link to={getArticleUrl()}>
          <h3 className={cn(
            "font-display font-bold text-headline",
            "group-hover:text-primary transition-colors",
            "leading-[var(--line-tight)]",
            isFeatured 
              ? "text-[length:var(--text-lg)] md:text-[length:var(--text-2xl)] lg:text-[length:var(--text-3xl)] mb-[var(--space-2)] md:mb-[var(--space-3)]" 
              : isCompact 
                ? "text-[length:var(--text-sm)] line-clamp-2" 
                : "text-[length:var(--text-lg)] mb-[var(--space-2)]"
          )}>
            {article.title}
          </h3>
        </Link>

        {/* Subtitle */}
        {article.subtitle && !isCompact && (
          <p className={cn(
            "text-muted-foreground line-clamp-2",
            "text-[length:var(--text-sm)] mb-[var(--space-2)]"
          )}>
            {article.subtitle}
          </p>
        )}

        {/* Excerpt - Token-based */}
        {showExcerpt && excerpt && !isCompact && (
          <p className={cn(
            "text-body-text line-clamp-2 md:line-clamp-3",
            "text-[length:var(--text-sm)]",
            isFeatured ? "mb-[var(--space-3)] md:mb-[var(--space-4)]" : "mb-[var(--space-2)] md:mb-[var(--space-3)]"
          )}>
            {excerpt}
          </p>
        )}

        {/* Meta - Using standardized MetaText */}
        {!isCompact && (
          <div className="mt-auto">
            <MetaText 
              author={article.author?.full_name}
              date={article.published_at}
              readTime={article.read_time_minutes}
              compact
            />
          </div>
        )}

        {/* OEM Tags - Hidden on mobile for cleaner UI */}
        {article.oems && article.oems.length > 0 && !isCompact && (
          <div className="mt-[var(--space-2)] md:mt-[var(--space-3)] hidden sm:flex flex-wrap gap-1">
            {article.oems.slice(0, 3).map((oem) => (
              <Badge 
                key={oem.id} 
                variant="outline" 
                className="text-[length:var(--text-xs)] bg-muted/50 border-border"
              >
                {oem.name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
});
